import { RRule } from 'rrule';

import EventTypeCalendar from 'domains/models/event_type_calendar';
import { FrequencyType } from 'domains/models/event_type_calendar/frequencyType';
import { getDateTime } from 'infras/datetime';
import { rruleDateTime } from 'infras/recurrence_rule';

export const convertCalendarToScheduleItem = (calendar: EventTypeCalendar) => {
  switch (calendar.frequencyType) {
    case FrequencyType.Monthly:
      return {
        year: null,
        yearInterval: 1,
        month: null,
        monthInterval: calendar.frequencyInterval ?? 1,
        wday: null,
        wdayInterval: 0,
        day: 1,
        dayInterval: 0,
        hour: 0,
        hourInterval: 0,
        minute: 0,
        minuteInterval: 0,
        exceptions: [],
      };
    case FrequencyType.Weekly:
      return {
        year: null,
        yearInterval: 1,
        month: null,
        monthInterval: 1,
        wday: calendar.wday,
        wdayInterval: calendar.frequencyInterval ?? 1,
        day: null,
        dayInterval: 0,
        hour: 0,
        hourInterval: 0,
        minute: 0,
        minuteInterval: 0,
        exceptions: [],
      };
    case FrequencyType.Daily:
      return {
        year: null,
        yearInterval: 1,
        month: null,
        monthInterval: 1,
        wday: null,
        wdayInterval: 0,
        day: null,
        dayInterval: calendar.frequencyInterval ?? 1,
        hour: 0,
        hourInterval: 0,
        minute: 0,
        minuteInterval: 0,
        exceptions: [],
      };
  }
};

export function convertCalendarToRRule(
  calendar: EventTypeCalendar,
  timeZone: string,
) {
  let rule: RRule | null = null;

  const interval = calendar.frequencyInterval ?? 0;
  const frequencyType = calendar.frequencyType ?? FrequencyType.Daily;

  const startedAt =
    frequencyType === FrequencyType.Weekly
      ? getDateTime(calendar.startedAt)
      : getDateTime(calendar.startedAt, timeZone);
  const endedAt =
    frequencyType === FrequencyType.Weekly
      ? getDateTime(calendar.endedAt)
      : getDateTime(calendar.endedAt, timeZone);
  const dtstart = rruleDateTime(
    startedAt.year,
    startedAt.month,
    startedAt.day,
    startedAt.hour,
    startedAt.minute,
    startedAt.second,
  );
  const until = calendar.endedAt
    ? rruleDateTime(
        endedAt.year,
        endedAt.month,
        endedAt.day,
        endedAt.hour,
        endedAt.minute,
        endedAt.second,
      )
    : undefined;
  if (until) {
    // [work around] rruleライブラリのバグで下記設定にしてもuntilの最終回9/26の分が返ってこないため1時間足して解消
    // FREQ=WEEKLY;INTERVAL=1;BYDAY=TU;UNTIL=20230926T000000T150000;DTSTART=20230904T150000;BYHOUR=16;BYMINUTE=0;
    // 上記の場合 9/5,9/12,/9/18しか返ってこないが1時間足すと9/26も返ってくる
    until.setHours(until.getHours() + 1);
  }
  switch (frequencyType) {
    case FrequencyType.Monthly:
      rule = new RRule({
        freq: RRule.MONTHLY,
        interval,
        // TODO: あたいがくるようになったら修正
        bymonthday: 1,
        byhour: calendar.startHour,
        byminute: calendar.startMinute,
        dtstart,
        until,
      });
      break;
    case FrequencyType.Weekly:
      const weekday = calendar.wday > 0 ? calendar.wday - 1 : 6;
      rule = new RRule({
        freq: RRule.WEEKLY,
        interval,
        // 元データが日曜=0月曜=1だったものをRRuleの月曜=0〜日曜=6に変換する処理
        byweekday: weekday,
        byhour: calendar.startHour,
        byminute: calendar.startMinute,
        dtstart,
        until,
        wkst: weekday === 0 ? RRule.SU : RRule.MO,
        tzid: timeZone,
      });
      break;
    case FrequencyType.Daily:
      rule = new RRule({
        freq: RRule.DAILY,
        interval,
        byhour: calendar.startHour,
        byminute: calendar.startMinute,
        dtstart,
        until,
        count: interval === 0 ? 1 : undefined,
      });
      break;
    default:
      return;
  }

  return rule;
}

export const filterCalendarAfterCurrentTime = (
  calendar: EventTypeCalendar[],
  timeZone: string,
) => {
  return calendar
    .filter((c) => {
      if (c.endedAt === null) {
        if (c.frequencyType === null) {
          const today = getDateTime(undefined, timeZone).startOf('day');
          const endedAt = getDateTime(c.startedAt, timeZone).set({
            hour: c.endHour,
            minute: c.endMinute,
          });

          return endedAt.toMillis() >= today.toMillis();
        } else {
          return true;
        }
      } else {
        const today = getDateTime(undefined, timeZone).startOf('day');
        const endedAt = getDateTime(c.endedAt, timeZone).set({
          hour: c.endHour,
          minute: c.endMinute,
        });

        return endedAt.toMillis() >= today.toMillis();
      }
    })
    .sort((a, b) => {
      const aStartedAt = getDateTime(a.startedAt, timeZone).set({
        hour: a.startHour,
        minute: a.startMinute,
      });
      const bStartedAt = getDateTime(b.startedAt, timeZone).set({
        hour: b.startHour,
        minute: b.startMinute,
      });

      return aStartedAt.toMillis() - bStartedAt.toMillis();
    });
};
